import { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import {
  Database,
  Activity,
  Droplets,
  Layers,
  ChevronDown,
  TrendingDown,
  TrendingUp,
} from 'lucide-react'
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts'
import { cn } from '@/lib/utils'
import {
  DISPLACEMENT_DATA,
  RAINFALL_DATA,
  SWI_DATA,
  NISCEMI_CENTER,
} from '@/lib/mockData'
import { formatDateShort } from '@/lib/utils'

// ----------------------------------------------------------
// Tab definitions
// ----------------------------------------------------------

type DataTab = 'insar' | 'rainfall' | 'swi'

const TABS: { id: DataTab; label: string; icon: React.ComponentType<{ size?: number | string; className?: string }> }[] = [
  { id: 'insar', label: 'InSAR Displacement', icon: Activity },
  { id: 'rainfall', label: 'Pluviometria', icon: Droplets },
  { id: 'swi', label: 'Soil Water Index', icon: Layers },
]

// ----------------------------------------------------------
// Animation helpers
// ----------------------------------------------------------

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.35 },
  }),
}

// ----------------------------------------------------------
// Area selector
// ----------------------------------------------------------

const AREAS = [
  { label: 'Niscemi (CL) — Caso Studio', lat: NISCEMI_CENTER.lat, lon: NISCEMI_CENTER.lon },
  { label: 'Modena Pianura (MO)', lat: 44.647, lon: 10.925 },
  { label: 'Argenta – Reno (FE)', lat: 44.613, lon: 11.829 },
  { label: 'Ancona Marche (AN)', lat: 43.615, lon: 13.518 },
]

// ----------------------------------------------------------
// Seeded pseudo-random for area-specific mock data
// ----------------------------------------------------------

function hashSeed(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
  return Math.abs(h)
}

function seededRandom(seed: number) {
  let s = seed
  return () => { s = (s * 16807 + 0) % 2147483647; return s / 2147483647 }
}

interface AreaProp {
  areaLabel: string
}

// ----------------------------------------------------------
// InSAR Chart
// ----------------------------------------------------------

function InsarChart({ areaLabel }: AreaProp) {
  const data = useMemo(() => {
    const seed = hashSeed(areaLabel)
    const rng = seededRandom(seed)
    const scale = 0.4 + rng() * 1.2 // 0.4x to 1.6x
    const velOffset = (rng() - 0.5) * 8
    return DISPLACEMENT_DATA.map((d) => ({
      date: formatDateShort(d.date),
      cumulative: parseFloat((d.cumulative * scale).toFixed(1)),
      velocity: parseFloat((d.velocity * scale + velOffset).toFixed(1)),
    }))
  }, [areaLabel])

  const last = data[data.length - 1]

  return (
    <div className="space-y-4">
      {/* Stats row */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Spostamento Cumulato', value: `${last?.cumulative.toFixed(1)} mm`, color: 'text-terra-purple', icon: TrendingDown },
          { label: 'Velocità Attuale', value: `${last?.velocity.toFixed(1)} mm/anno`, color: 'text-terra-red', icon: TrendingDown },
          { label: 'Periodo Analisi', value: '9 mesi', color: 'text-terra-blue', icon: Activity },
        ].map(({ label, value, color, icon: Icon }) => (
          <div key={label} className="glass rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Icon size={13} className={color} />
              <span className="text-xs text-slate-500">{label}</span>
            </div>
            <div className={cn('text-lg font-bold', color)}>{value}</div>
          </div>
        ))}
      </div>

      {/* Cumulative displacement */}
      <div>
        <p className="text-xs text-slate-400 mb-2 font-medium">Spostamento Cumulato (LOS) — mm</p>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -10 }}>
            <defs>
              <linearGradient id="dispGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(51,65,85,0.4)" />
            <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} interval={5} />
            <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={{ background: 'rgba(15,23,42,0.95)', border: '1px solid rgba(139,92,246,0.3)', borderRadius: '8px', fontSize: '12px', color: '#e2e8f0' }}
              formatter={(val: number) => [`${val.toFixed(2)} mm`, 'Spostamento']}
            />
            <Area type="monotone" dataKey="cumulative" stroke="#8b5cf6" strokeWidth={2} fill="url(#dispGrad)" dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Velocity */}
      <div>
        <p className="text-xs text-slate-400 mb-2 font-medium">Velocità di Spostamento — mm/anno</p>
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(51,65,85,0.4)" />
            <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} interval={5} />
            <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={{ background: 'rgba(15,23,42,0.95)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', fontSize: '12px', color: '#e2e8f0' }}
              formatter={(val: number) => [`${val.toFixed(1)} mm/yr`, 'Velocità']}
            />
            <ReferenceLine y={-20} stroke="#ef4444" strokeDasharray="4 4" strokeOpacity={0.6} label={{ value: 'Soglia critica', fill: '#ef4444', fontSize: 10 }} />
            <Line type="monotone" dataKey="velocity" stroke="#ef4444" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Satellite info */}
      <div className="flex items-center gap-4 text-xs text-slate-500 pt-2 border-t border-slate-800/60">
        <span>Fonte: Sentinel-1C (ESA)</span>
        <span>Track: 117 Ascending</span>
        <span>Coerenza media: 0.72</span>
        <span>Metodo: SBAS</span>
      </div>
    </div>
  )
}

// ----------------------------------------------------------
// Rainfall chart
// ----------------------------------------------------------

function RainfallChart({ areaLabel }: AreaProp) {
  const { data, last24, cum30 } = useMemo(() => {
    const seed = hashSeed(areaLabel + 'rain')
    const rng = seededRandom(seed)
    const scale = 0.5 + rng() * 1.0
    const slice = RAINFALL_DATA.slice(-90)
    const mapped = slice.map((d) => {
      const v = parseFloat((d.value * scale + (rng() - 0.5) * 8).toFixed(1))
      const val = Math.max(0, v)
      return { date: formatDateShort(d.date), value: val, cumulative30d: parseFloat((d.cumulative30d * scale).toFixed(0)) }
    })
    const l = mapped[mapped.length - 1]
    return { data: mapped, last24: l?.value.toFixed(0) ?? '0', cum30: l?.cumulative30d.toFixed(0) ?? '0' }
  }, [areaLabel])

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Pioggia 24h', value: `${last24} mm`, color: 'text-terra-blue', icon: TrendingUp },
          { label: 'Cumulata 30g', value: `${cum30} mm`, color: 'text-terra-orange', icon: TrendingUp },
          { label: 'Soglia Allerta Tc3', value: '120 mm/24h', color: 'text-terra-red', icon: Activity },
        ].map(({ label, value, color, icon: Icon }) => (
          <div key={label} className="glass rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Icon size={13} className={color} />
              <span className="text-xs text-slate-500">{label}</span>
            </div>
            <div className={cn('text-lg font-bold', color)}>{value}</div>
          </div>
        ))}
      </div>

      <div>
        <p className="text-xs text-slate-400 mb-2 font-medium">Precipitazioni Giornaliere — mm/giorno (ultimi 90gg)</p>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -10 }}>
            <defs>
              <linearGradient id="rainGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(51,65,85,0.4)" />
            <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} interval={9} />
            <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={{ background: 'rgba(15,23,42,0.95)', border: '1px solid rgba(14,165,233,0.3)', borderRadius: '8px', fontSize: '12px', color: '#e2e8f0' }}
              formatter={(val: number) => [`${val.toFixed(1)} mm`, 'Pioggia']}
            />
            <ReferenceLine y={120} stroke="#ef4444" strokeDasharray="4 4" strokeOpacity={0.7} />
            <Area type="monotone" dataKey="value" stroke="#0ea5e9" strokeWidth={2} fill="url(#rainGrad)" dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div>
        <p className="text-xs text-slate-400 mb-2 font-medium">Precipitazione Cumulata 30 giorni — mm</p>
        <ResponsiveContainer width="100%" height={160}>
          <LineChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(51,65,85,0.4)" />
            <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} interval={9} />
            <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={{ background: 'rgba(15,23,42,0.95)', border: '1px solid rgba(249,115,22,0.3)', borderRadius: '8px', fontSize: '12px', color: '#e2e8f0' }}
              formatter={(val: number) => [`${val.toFixed(0)} mm`, 'Cumulata 30g']}
            />
            <Line type="monotone" dataKey="cumulative30d" stroke="#f97316" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="flex items-center gap-4 text-xs text-slate-500 pt-2 border-t border-slate-800/60">
        <span>Fonte: ARPA Regionale</span>
        <span>Stazione: {areaLabel.split('(')[0].trim()}</span>
        <span>Interpolazione: IDW/Kriging</span>
      </div>
    </div>
  )
}

// ----------------------------------------------------------
// SWI chart
// ----------------------------------------------------------

function SWIChart({ areaLabel }: AreaProp) {
  const { data, currentSwi, currentAnomaly } = useMemo(() => {
    const seed = hashSeed(areaLabel + 'swi')
    const rng = seededRandom(seed)
    const offset = (rng() - 0.5) * 0.25
    const scale = 0.7 + rng() * 0.6
    const slice = SWI_DATA.slice(-90)
    const mapped = slice.map((d) => {
      const v = Math.min(0.98, Math.max(0.1, d.value * scale + offset))
      const a = parseFloat(((d.anomaly * scale) + offset * 0.3).toFixed(3))
      return { date: formatDateShort(d.date), value: parseFloat(v.toFixed(3)), anomaly: a }
    })
    const l = mapped[mapped.length - 1]
    return { data: mapped, currentSwi: l?.value.toFixed(2) ?? '0', currentAnomaly: (l?.anomaly ?? 0) > 0 ? `+${l?.anomaly.toFixed(2)}` : l?.anomaly.toFixed(2) ?? '0' }
  }, [areaLabel])

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'SWI Attuale', value: currentSwi, color: 'text-terra-orange', icon: Activity },
          { label: 'Anomalia', value: currentAnomaly, color: 'text-terra-red', icon: TrendingUp },
          { label: 'Soglia Critica', value: '> 0.85', color: 'text-terra-yellow', icon: Activity },
        ].map(({ label, value, color, icon: Icon }) => (
          <div key={label} className="glass rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Icon size={13} className={color} />
              <span className="text-xs text-slate-500">{label}</span>
            </div>
            <div className={cn('text-lg font-bold', color)}>{value}</div>
          </div>
        ))}
      </div>

      <div>
        <p className="text-xs text-slate-400 mb-2 font-medium">Soil Water Index (0–1) — ultimi 90gg</p>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -10 }}>
            <defs>
              <linearGradient id="swiGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(51,65,85,0.4)" />
            <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} interval={9} />
            <YAxis domain={[0, 1]} tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={{ background: 'rgba(15,23,42,0.95)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '8px', fontSize: '12px', color: '#e2e8f0' }}
              formatter={(val: number) => [val.toFixed(3), 'SWI']}
            />
            <ReferenceLine y={0.85} stroke="#ef4444" strokeDasharray="4 4" strokeOpacity={0.6} />
            <Area type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2} fill="url(#swiGrad)" dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div>
        <p className="text-xs text-slate-400 mb-2 font-medium">Anomalia SWI (deviazione da climatologia)</p>
        <ResponsiveContainer width="100%" height={150}>
          <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -10 }}>
            <defs>
              <linearGradient id="anomGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(51,65,85,0.4)" />
            <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} interval={9} />
            <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={{ background: 'rgba(15,23,42,0.95)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '8px', fontSize: '12px', color: '#e2e8f0' }}
              formatter={(val: number) => [val.toFixed(3), 'Anomalia']}
            />
            <ReferenceLine y={0} stroke="#475569" />
            <Area type="monotone" dataKey="anomaly" stroke="#f59e0b" strokeWidth={2} fill="url(#anomGrad)" dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="flex items-center gap-4 text-xs text-slate-500 pt-2 border-t border-slate-800/60">
        <span>Fonte: Copernicus GLS</span>
        <span>Prodotto: SWI-10</span>
        <span>Risoluzione: 1 km</span>
      </div>
    </div>
  )
}

// ----------------------------------------------------------
// Page
// ----------------------------------------------------------

export default function DataExplorerPage() {
  const [activeTab, setActiveTab] = useState<DataTab>('insar')
  const [selectedArea, setSelectedArea] = useState(AREAS[0])
  const [areaOpen, setAreaOpen] = useState(false)

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white">Esplora Dati</h1>
            <p className="text-sm text-slate-400 mt-0.5">
              Serie storiche InSAR, pluviometria e indici di umidità del suolo
            </p>
          </div>

          {/* Area selector */}
          <div className="relative">
            <button
              onClick={() => setAreaOpen((o) => !o)}
              className="flex items-center gap-2 px-4 py-2 glass border border-terra-blue/20 rounded-lg text-sm text-slate-300 hover:text-white transition-colors"
            >
              <Database size={14} className="text-terra-blue" />
              {selectedArea.label}
              <ChevronDown size={13} className={cn('transition-transform', areaOpen && 'rotate-180')} />
            </button>

            {areaOpen && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute right-0 top-full mt-2 w-72 z-50 glass-strong rounded-xl overflow-hidden shadow-2xl"
              >
                {AREAS.map((area) => (
                  <button
                    key={area.label}
                    onClick={() => { setSelectedArea(area); setAreaOpen(false) }}
                    className={cn(
                      'w-full text-left px-4 py-2.5 text-xs transition-colors',
                      selectedArea.label === area.label
                        ? 'bg-terra-blue/15 text-terra-blue'
                        : 'text-slate-300 hover:bg-slate-800/60'
                    )}
                  >
                    {area.label}
                  </button>
                ))}
              </motion.div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Coords badge */}
      <motion.div
        custom={1}
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        className="flex items-center gap-4 text-xs text-slate-500"
      >
        <span className="font-mono">
          Lat {selectedArea.lat.toFixed(4)} · Lon {selectedArea.lon.toFixed(4)}
        </span>
        <span className="text-slate-700">|</span>
        <span>Coordinate: WGS84 / EPSG:4326</span>
      </motion.div>

      {/* Tab bar */}
      <motion.div
        custom={2}
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        className="flex gap-1 p-1 glass rounded-xl w-fit"
      >
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200',
              activeTab === id
                ? 'bg-terra-dark border border-terra-blue/30 text-terra-blue shadow-inner'
                : 'text-slate-400 hover:text-slate-200'
            )}
          >
            <Icon size={14} />
            {label}
          </button>
        ))}
      </motion.div>

      {/* Chart panel */}
      <motion.div
        key={`${activeTab}-${selectedArea.label}`}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="terra-card"
      >
        <div className="flex items-center gap-2 mb-6">
          {activeTab === 'insar' && <Activity size={16} className="text-terra-purple" />}
          {activeTab === 'rainfall' && <Droplets size={16} className="text-terra-blue" />}
          {activeTab === 'swi' && <Layers size={16} className="text-terra-green" />}
          <h2 className="text-sm font-bold text-white">
            {activeTab === 'insar' && 'Spostamento InSAR Sentinel-1'}
            {activeTab === 'rainfall' && 'Serie Pluviometrica ARPA'}
            {activeTab === 'swi' && 'Soil Water Index Copernicus'}
          </h2>
          <span className="ml-2 text-xs text-slate-500">· {selectedArea.label.split('—')[0].trim()}</span>
        </div>

        {activeTab === 'insar' && <InsarChart areaLabel={selectedArea.label} />}
        {activeTab === 'rainfall' && <RainfallChart areaLabel={selectedArea.label} />}
        {activeTab === 'swi' && <SWIChart areaLabel={selectedArea.label} />}
      </motion.div>
    </div>
  )
}
